/**
 * Write-time validation. Nothing enters IndexedDB unchecked: a corrupt record written today is
 * a crash on every future load, so every repository funnels its input through here first.
 */

import {
  AppError,
  CANONICAL_BONE_IDS,
  TWEEN_PRESETS,
  isCanonicalBoneId,
  normalizeClip,
  type CanonicalAnimationClip,
  type CanonicalBoneId,
  type MappingPreset,
  type ProjectRecord,
  type RotationKeyframe,
  type TweenPreset,
} from '../shared';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isInteger(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && Number.isInteger(v);
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

function fail(code: string, message: string, details?: unknown): never {
  throw new AppError(code, message, details);
}

/**
 * Validate a clip and return the canonical, storage-ready form (`normalizeClip`: sorted unique
 * integer frames, normalized quaternions, duration covering the last key).
 *
 * Throws `AppError('INVALID_CLIP')` — callers rely on this to keep a batch write atomic by
 * validating everything before the transaction opens.
 */
export function normalizeClipForStorage(clip: CanonicalAnimationClip): CanonicalAnimationClip {
  if (!isPlainObject(clip)) fail('INVALID_CLIP', 'Animation clip must be an object.', clip);

  const where = isNonEmptyString(clip.id) ? `Clip "${clip.id}"` : 'Clip';

  if (!isNonEmptyString(clip.id)) fail('INVALID_CLIP', 'Animation clip has no id.', clip);
  if (typeof clip.name !== 'string') fail('INVALID_CLIP', `${where} has a non-string name.`, clip);
  // A name is an identifier the runtime and the GLB exporter both use; blank is not one of them.
  if (clip.name.trim().length === 0) fail('INVALID_CLIP', `${where} has a blank name.`, clip);
  if (!isInteger(clip.fps) || clip.fps < 1 || clip.fps > 240) {
    fail('INVALID_CLIP', `${where} has an invalid fps (${String(clip.fps)}).`, clip);
  }
  if (!isInteger(clip.durationFrames) || clip.durationFrames < 0) {
    fail(
      'INVALID_CLIP',
      `${where} has an invalid duration (${String(clip.durationFrames)} frames).`,
      clip,
    );
  }
  if (typeof clip.loop !== 'boolean') fail('INVALID_CLIP', `${where} has a non-boolean loop.`, clip);
  if (!isPlainObject(clip.tracks)) {
    fail('INVALID_CLIP', `${where} has no track map.`, clip);
  }

  for (const boneKey of Object.keys(clip.tracks)) {
    if (!isCanonicalBoneId(boneKey)) {
      fail('INVALID_CLIP', `${where} has a track for unknown bone "${boneKey}".`, clip);
    }
    const track: unknown = clip.tracks[boneKey as CanonicalBoneId];
    if (!isPlainObject(track) || !Array.isArray(track.keyframes)) {
      fail('INVALID_CLIP', `${where} track "${boneKey}" has no keyframe array.`, clip);
    }
    for (const forbidden of ['position', 'scale']) {
      if (forbidden in track) {
        fail(
          'INVALID_CLIP',
          `${where} track "${boneKey}" carries "${forbidden}" data — clips are rotation-only.`,
          clip,
        );
      }
    }
    for (const raw of track.keyframes as unknown[]) {
      assertKeyframe(raw, where, boneKey, clip);
    }
  }

  if (clip.events !== undefined && !Array.isArray(clip.events)) {
    fail('INVALID_CLIP', `${where} has an "events" that is not an array.`, clip);
  }

  const normalized = normalizeClip(clip);
  // `normalizeClip` keeps unknown extra fields via spread; re-project onto the contract shape so
  // stored records stay exactly the pinned type.
  //
  // THIS RE-PROJECTION IS WHERE NEW CLIP FIELDS GO TO DIE. Anything not named here is dropped on
  // every save, silently — the clip in memory keeps it, the clip on disk does not, and the loss
  // only shows up after a reload. `events` is spelled out below for that reason; the next field
  // added to `CanonicalAnimationClip` needs the same line.
  //
  // A field on a KEYFRAME (`hipsY`) is re-projected one level down, by `normalizeClip`'s per-key
  // rebuild in `shared/clipOps.ts` — `tracks` arrives here already rebuilt, so it must be named
  // there instead. Same trap, different file.
  const out: CanonicalAnimationClip = {
    id: normalized.id,
    name: normalized.name.trim(),
    fps: normalized.fps,
    durationFrames: normalized.durationFrames,
    loop: normalized.loop,
    tracks: normalized.tracks,
    createdAt: typeof normalized.createdAt === 'number' ? normalized.createdAt : Date.now(),
    updatedAt: typeof normalized.updatedAt === 'number' ? normalized.updatedAt : Date.now(),
  };
  // Omitted rather than stored as `[]`, so a clip with no events round-trips byte-identically.
  if (normalized.events && normalized.events.length > 0) out.events = normalized.events;
  return out;
}

function assertKeyframe(raw: unknown, where: string, bone: string, clip: unknown): void {
  if (!isPlainObject(raw)) {
    fail('INVALID_CLIP', `${where} track "${bone}" has a non-object keyframe.`, clip);
  }
  const key = raw as unknown as RotationKeyframe;
  if (!isInteger(key.frame) || key.frame < 0) {
    fail(
      'INVALID_CLIP',
      `${where} track "${bone}" has a keyframe at an invalid frame (${String(key.frame)}).`,
      clip,
    );
  }
  const q: unknown = key.rotation;
  if (!Array.isArray(q) || q.length !== 4) {
    fail('INVALID_CLIP', `${where} track "${bone}" frame ${key.frame}: rotation is not [x,y,z,w].`, clip);
  }
  for (const component of q as unknown[]) {
    if (typeof component !== 'number' || !Number.isFinite(component)) {
      fail(
        'INVALID_CLIP',
        `${where} track "${bone}" frame ${key.frame}: rotation has a non-finite component.`,
        clip,
      );
    }
  }
  const length = Math.hypot(q[0] as number, q[1] as number, q[2] as number, q[3] as number);
  if (length === 0) {
    fail(
      'INVALID_CLIP',
      `${where} track "${bone}" frame ${key.frame}: rotation is the zero quaternion.`,
      clip,
    );
  }
  if (!TWEEN_PRESETS.includes(key.easing as TweenPreset)) {
    fail(
      'INVALID_CLIP',
      `${where} track "${bone}" frame ${key.frame}: unknown easing ${JSON.stringify(key.easing)}.`,
      clip,
    );
  }
  // A NaN here would be stored, reloaded, and then displace the character by a non-number on every
  // frame of the clip — the failure would surface far from the write that caused it.
  if (key.hipsY !== undefined && (typeof key.hipsY !== 'number' || !Number.isFinite(key.hipsY))) {
    fail(
      'INVALID_CLIP',
      `${where} track "${bone}" frame ${key.frame}: "hipsY" must be a finite number.`,
      clip,
    );
  }
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** `length` numbers, every one of them finite. `[NaN, 0, 0]` is not a position. */
function isFiniteTuple(v: unknown, length: number): boolean {
  return Array.isArray(v) && v.length === length && v.every(isFiniteNumber);
}

/** A usable rotation: four finite components that are not all zero. */
function isRotationTuple(v: unknown): boolean {
  if (!isFiniteTuple(v, 4)) return false;
  const q = v as number[];
  return Math.hypot(q[0], q[1], q[2], q[3]) > 0;
}

/**
 * Array-like of `length` numeric entries — `skinIndex` / `skinWeight` come back from IndexedDB as
 * typed arrays, but a record hand-built in a test or by an older build may hold plain arrays.
 */
function isNumericArrayLike(v: unknown, length: number): boolean {
  if (ArrayBuffer.isView(v) && !(v instanceof DataView)) {
    return (v as unknown as { length: number }).length === length;
  }
  return Array.isArray(v) && v.length === length && v.every(isFiniteNumber);
}

function assertBinding(binding: unknown, where: string, record: unknown): void {
  if (!isPlainObject(binding)) fail('INVALID_PROJECT', `${where} has a non-object binding.`, record);
  if (!isNonEmptyString(binding.rigVersion)) {
    fail('INVALID_PROJECT', `${where} binding has no rig version.`, record);
  }
  if (!isPlainObject(binding.canonicalToSourceBone)) {
    fail('INVALID_PROJECT', `${where} binding has no bone map.`, record);
  }
  for (const [bone, sourceName] of Object.entries(binding.canonicalToSourceBone)) {
    if (!isCanonicalBoneId(bone)) {
      fail('INVALID_PROJECT', `${where} binding maps unknown canonical bone "${bone}".`, record);
    }
    if (sourceName !== undefined && !isNonEmptyString(sourceName)) {
      fail('INVALID_PROJECT', `${where} binding maps "${bone}" to a blank source bone.`, record);
    }
  }
  if (!isRotationTuple(binding.worldCorrection)) {
    fail('INVALID_PROJECT', `${where} binding has an invalid world correction.`, record);
  }
  // Both offset maps are optional and both hold quaternions; an unusable one silently changes
  // every pose the rig plays, so they are checked as strictly as the correction above.
  for (const field of ['restRotationOffsets', 'restAlignment'] as const) {
    const map: unknown = binding[field];
    if (map === undefined) continue;
    if (!isPlainObject(map)) {
      fail('INVALID_PROJECT', `${where} binding has a non-object ${field}.`, record);
    }
    for (const [bone, q] of Object.entries(map)) {
      if (!isCanonicalBoneId(bone)) {
        fail('INVALID_PROJECT', `${where} binding ${field} names unknown bone "${bone}".`, record);
      }
      if (q !== undefined && !isRotationTuple(q)) {
        fail('INVALID_PROJECT', `${where} binding ${field}["${bone}"] is not a rotation.`, record);
      }
    }
  }
}

function assertPlacement(placement: unknown, where: string, record: unknown): void {
  if (!isPlainObject(placement)) {
    fail('INVALID_PROJECT', `${where} has a non-object placement.`, record);
  }
  if (!isNonEmptyString(placement.rigVersion)) {
    fail('INVALID_PROJECT', `${where} placement has no rig version.`, record);
  }
  if (!isPlainObject(placement.jointWorldPositions)) {
    fail('INVALID_PROJECT', `${where} placement has no joint positions.`, record);
  }
  for (const bone of CANONICAL_BONE_IDS) {
    const p: unknown = placement.jointWorldPositions[bone];
    // Checking the length alone let `[NaN, NaN, NaN]` through, and a non-finite joint position
    // propagates into the skeleton build as an invisible NaN cascade (audit P1).
    if (p !== undefined && !isFiniteTuple(p, 3)) {
      fail('INVALID_PROJECT', `${where} placement for "${bone}" is not a finite Vec3.`, record);
    }
  }
}

function assertSkinData(skinData: unknown, where: string, record: unknown): void {
  if (!Array.isArray(skinData)) {
    fail('INVALID_PROJECT', `${where} has a non-array skinData.`, record);
  }
  for (const entry of skinData as unknown[]) {
    if (!isPlainObject(entry)) {
      fail('INVALID_PROJECT', `${where} has a non-object skinData entry.`, record);
    }
    if (typeof entry.meshName !== 'string') {
      fail('INVALID_PROJECT', `${where} has a skinData entry without a mesh name.`, record);
    }
    if (!isInteger(entry.vertexCount) || entry.vertexCount < 0) {
      fail(
        'INVALID_PROJECT',
        `${where} skinData for "${entry.meshName}" has an invalid vertex count.`,
        record,
      );
    }
    // three.js skin attributes are 4 influences per vertex; a wrong length is a mesh/weight
    // mismatch that only surfaces as a garbled deformation at play time.
    const expected = (entry.vertexCount as number) * 4;
    for (const field of ['skinIndex', 'skinWeight'] as const) {
      if (!isNumericArrayLike(entry[field], expected)) {
        fail(
          'INVALID_PROJECT',
          `${where} skinData for "${entry.meshName}" has a ${field} of the wrong length ` +
            `(expected ${expected}).`,
          record,
        );
      }
    }
  }
}

/**
 * Validate a project record before it is written. Throws `AppError('INVALID_PROJECT')`.
 *
 * Deliberately as strict as the clip validator (audit P1): a project record is long-lived, is
 * read back on every launch, and every field below is one the reopen path dereferences without
 * asking twice. The cheap moment to reject a bad value is here, once, not on every future load.
 */
export function assertStorableProject(record: ProjectRecord): void {
  if (!isPlainObject(record)) fail('INVALID_PROJECT', 'Project record must be an object.', record);
  if (!isNonEmptyString(record.id)) fail('INVALID_PROJECT', 'Project record has no id.', record);

  const where = `Project "${record.id}"`;

  if (typeof record.name !== 'string') {
    fail('INVALID_PROJECT', `${where} has a non-string name.`, record);
  }
  if (record.name.trim().length === 0) {
    fail('INVALID_PROJECT', `${where} has a blank name.`, record);
  }
  if (record.kind !== 'rigged' && record.kind !== 'unrigged') {
    fail('INVALID_PROJECT', `${where} has an unknown kind.`, record);
  }
  for (const field of ['createdAt', 'updatedAt'] as const) {
    if (!isFiniteNumber(record[field]) || record[field] < 0) {
      fail('INVALID_PROJECT', `${where} has an invalid ${field}.`, record);
    }
  }
  // Optional by design: records written before the field existed simply have no "last opened"
  // time. A present value is held to the same standard as the two above — NaN or a negative
  // timestamp would sort the project list into nonsense rather than fail loudly.
  if (record.lastOpenedAt !== undefined) {
    if (!isFiniteNumber(record.lastOpenedAt) || record.lastOpenedAt < 0) {
      fail('INVALID_PROJECT', `${where} has an invalid lastOpenedAt.`, record);
    }
  }
  if (!isNonEmptyString(record.sourceGlbBlobId)) {
    fail('INVALID_PROJECT', `${where} has no source GLB reference.`, record);
  }
  if (record.riggedGlbBlobId !== undefined && !isNonEmptyString(record.riggedGlbBlobId)) {
    fail('INVALID_PROJECT', `${where} has a blank rigged GLB reference.`, record);
  }
  if (
    !Array.isArray(record.selectedClipIds) ||
    record.selectedClipIds.some((c) => !isNonEmptyString(c))
  ) {
    fail('INVALID_PROJECT', `${where} has an invalid clip selection.`, record);
  }
  if (record.binding !== null && record.binding !== undefined) {
    assertBinding(record.binding, where, record);
  }
  if (record.placement !== null && record.placement !== undefined) {
    assertPlacement(record.placement, where, record);
  }
  if (record.skinData !== undefined) assertSkinData(record.skinData, where, record);
  if (record.skinParams !== undefined && !isPlainObject(record.skinParams)) {
    fail('INVALID_PROJECT', `${where} has a non-object skinParams.`, record);
  }
}

/** What `normalizeProjectForRead` had to do to a stored record to make it usable. */
export interface ProjectReadReport {
  record: ProjectRecord | null;
  /** Human-readable notes; empty when the record was already clean. */
  problems: string[];
}

/**
 * Read-time triage for a stored project (audit P1).
 *
 * Write-time validation only protects records written by a build that has it. A database can
 * still hold a record from an older version, or one damaged by a half-finished write, and the
 * reopen path must not turn that into a crash on launch. So:
 *
 *  - a record missing its identity (id, kind, source GLB, clip selection) is **quarantined** —
 *    `record: null`, and the caller drops it from the project list rather than offering to open
 *    something that cannot open;
 *  - a record whose optional rig state is corrupt is **repaired** — the bad binding, placement or
 *    weight set is dropped, and the project reopens in the state it would have had before that
 *    step, which the user can redo.
 */
export function normalizeProjectForRead(raw: unknown): ProjectReadReport {
  const problems: string[] = [];
  if (!isPlainObject(raw)) return { record: null, problems: ['the record is not an object'] };

  const record = raw as unknown as ProjectRecord;
  if (
    !isNonEmptyString(record.id) ||
    (record.kind !== 'rigged' && record.kind !== 'unrigged') ||
    !isNonEmptyString(record.sourceGlbBlobId)
  ) {
    return { record: null, problems: ['the record has no usable id, kind or model reference'] };
  }

  const next: ProjectRecord = { ...record };

  if (typeof next.name !== 'string' || next.name.trim().length === 0) {
    next.name = 'Untitled model';
    problems.push('the project name was missing and was replaced');
  }
  for (const field of ['createdAt', 'updatedAt'] as const) {
    if (!isFiniteNumber(next[field]) || next[field] < 0) {
      next[field] = 0;
      problems.push(`${field} was missing`);
    }
  }
  // Absent is the normal case (older builds) and says nothing — only a present-but-unusable value
  // is a problem, and dropping it costs the user a line of metadata, never their work.
  if (next.lastOpenedAt !== undefined) {
    if (!isFiniteNumber(next.lastOpenedAt) || next.lastOpenedAt < 0) {
      delete next.lastOpenedAt;
      problems.push('the last-opened time was unreadable and was dropped');
    }
  }
  if (!Array.isArray(next.selectedClipIds)) {
    next.selectedClipIds = [];
    problems.push('the animation selection was unreadable and was cleared');
  } else {
    const kept = next.selectedClipIds.filter((id) => isNonEmptyString(id));
    if (kept.length !== next.selectedClipIds.length) {
      problems.push('some entries of the animation selection were unreadable and were dropped');
    }
    next.selectedClipIds = kept;
  }

  const drop = (field: 'binding' | 'placement', assert: () => void, note: string): void => {
    try {
      assert();
    } catch {
      problems.push(note);
      next[field] = null;
    }
  };
  if (next.binding !== null && next.binding !== undefined) {
    drop('binding', () => assertBinding(next.binding, 'record', next), 'the bone mapping was corrupt and was dropped — re-confirm the mapping');
  } else {
    next.binding = null;
  }
  if (next.placement !== null && next.placement !== undefined) {
    drop('placement', () => assertPlacement(next.placement, 'record', next), 'the skeleton placement was corrupt and was dropped — place the skeleton again');
  } else {
    next.placement = null;
  }

  if (next.skinData !== undefined) {
    try {
      assertSkinData(next.skinData, 'record', next);
    } catch {
      delete next.skinData;
      problems.push('the skin weights were corrupt and were dropped — generate them again');
    }
  }
  // Weights without a placement are not corrupt, only unusable until one exists — the reopen
  // path already ignores them, and deleting the user's solve over a consistency rule would be
  // this function causing exactly the data loss it exists to prevent.
  if (next.skinParams !== undefined && !isPlainObject(next.skinParams)) {
    delete next.skinParams;
    problems.push('the skin parameters were unreadable and were reset to defaults');
  }

  return { record: next, problems };
}

/** Validate a mapping preset before it is written. Throws `AppError('INVALID_PRESET')`. */
export function assertStorablePreset(preset: MappingPreset): void {
  if (!isPlainObject(preset)) fail('INVALID_PRESET', 'Mapping preset must be an object.', preset);
  if (!isNonEmptyString(preset.id)) fail('INVALID_PRESET', 'Mapping preset has no id.', preset);
  if (!isNonEmptyString(preset.name)) fail('INVALID_PRESET', 'Mapping preset has no name.', preset);
  if (!isNonEmptyString(preset.rigVersion)) {
    fail('INVALID_PRESET', `Mapping preset "${preset.name}" has no rig version.`, preset);
  }
  if (!isPlainObject(preset.canonicalToSourceBone)) {
    fail('INVALID_PRESET', `Mapping preset "${preset.name}" has no bone map.`, preset);
  }
  for (const boneKey of Object.keys(preset.canonicalToSourceBone)) {
    if (!isCanonicalBoneId(boneKey)) {
      fail(
        'INVALID_PRESET',
        `Mapping preset "${preset.name}" maps unknown canonical bone "${boneKey}".`,
        preset,
      );
    }
  }
}
